import React, { useCallback, useEffect, useRef, useState } from "react";

// Shows the org banner: a muted, looping video when one is configured, with
// the banner image as its poster and fallback; otherwise just the image.
//
// The image is used instead of the video when the viewer prefers reduced
// motion or has data saver on, and if the video fails to load (a broken or
// non-video link). With neither, `fallback` renders. The video pauses while
// scrolled out of view.
//
// `zoom` (0-100) runs from "whole media visible" (0) to "fill the banner"
// (100, the default). CSS object-fit only has those two end points, so below
// 100 the element is sized and placed to exactly the visible media rectangle
// (scaled from the `contain` size towards the `cover` size, anchored at
// `position`). The part of it inside the banner is clipped with rounded
// corners (`radius`, px), so the visible media is rounded at every zoom level
// even where it overflows the banner; the space around it shows the page
// behind. The parent must be `position: relative` with `overflow: hidden`.
const prefersStill = () => {
  if (typeof window === "undefined") return false;
  const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  const saveData = navigator.connection?.saveData;
  return Boolean(reducedMotion || saveData);
};

// Where the media sits inside the banner box at a given zoom, in px.
export const zoomedRect = (box, media, zoom, position) => {
  const contain = Math.min(box.w / media.w, box.h / media.h);
  const cover = Math.max(box.w / media.w, box.h / media.h);
  const scale = contain + (cover - contain) * (zoom / 100);
  const width = media.w * scale;
  const height = media.h * scale;
  // Media relatively taller than the box is cropped top/bottom as it zooms,
  // so the position slider picks the anchor; wider media stays centred.
  const tallerThanBox = media.w / media.h < box.w / box.h;
  const anchorY = tallerThanBox ? (position ?? 50) / 100 : 0.5;
  return {
    width,
    height,
    left: (box.w - width) / 2,
    top: (box.h - height) * anchorY,
  };
};

const BannerMedia = ({
  imageUrl,
  videoUrl,
  position = 50,
  zoom = 100,
  alt = "",
  className = "",
  radius = 16,
  fallback = null,
}) => {
  const elRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const [still] = useState(prefersStill);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [media, setMedia] = useState({ w: 0, h: 0 });

  // A new link gets a fresh chance to load.
  useEffect(() => setFailed(false), [videoUrl]);

  const showVideo = Boolean(videoUrl) && !failed && !still;
  const zoomLevel = Math.min(100, Math.max(0, zoom ?? 100));
  const fill = zoomLevel >= 100;

  // Pause the video while it is off screen.
  useEffect(() => {
    const video = elRef.current;
    if (!showVideo || !video || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        // play() rejects if the browser blocks autoplay; the poster stays up.
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
    observer.observe(video);
    return () => observer.disconnect();
  }, [showVideo, videoUrl]);

  // Track the banner box (the parent) when zoomed out; the element itself is
  // sized from it, so it can't be the thing observed.
  useEffect(() => {
    const parent = elRef.current?.parentElement;
    if (fill || !parent || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setBox({ w: width, h: height });
    });
    observer.observe(parent);
    return () => observer.disconnect();
  }, [fill, showVideo, imageUrl, videoUrl]);

  const onImageLoad = useCallback((e) => {
    setMedia({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight });
  }, []);
  const onVideoMetadata = useCallback((e) => {
    setMedia({ w: e.currentTarget.videoWidth, h: e.currentTarget.videoHeight });
  }, []);

  const objectPosition = `center ${position ?? 50}%`;
  const measured = box.w > 0 && box.h > 0 && media.w > 0 && media.h > 0;
  let style;
  if (fill) {
    style = { objectPosition };
  } else if (measured) {
    const rect = zoomedRect(box, media, zoomLevel, position);
    // Inset the clip by however far the media overflows each banner edge.
    const inset = [
      Math.max(0, -rect.top),
      Math.max(0, rect.left + rect.width - box.w),
      Math.max(0, rect.top + rect.height - box.h),
      Math.max(0, -rect.left),
    ];
    style = {
      position: "absolute",
      left: `${rect.left}px`,
      top: `${rect.top}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
      objectFit: "cover",
      maxWidth: "none",
      clipPath: `inset(${inset.map((v) => `${v}px`).join(" ")} round ${radius}px)`,
    };
  } else {
    // Until sizes are known, show the whole media rather than a wrong crop.
    style = { objectFit: "contain" };
  }

  if (showVideo) {
    return (
      <video
        ref={elRef}
        key={videoUrl}
        src={videoUrl}
        poster={imageUrl || undefined}
        className={className}
        style={style}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        aria-hidden="true"
        onLoadedMetadata={onVideoMetadata}
        onError={() => setFailed(true)}
      />
    );
  }

  if (!imageUrl) return fallback;
  return (
    <img
      ref={elRef}
      src={imageUrl}
      alt={alt}
      className={className}
      style={style}
      onLoad={onImageLoad}
    />
  );
};

export default BannerMedia;
